/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback
} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import HeaderButton from '../grid/HeaderButton';
import {BulkHeader} from '../grid/IonGrid';
import SelectionHeaderButtons from '../grid/SelectionHeaderButtons';
import SelectionHeaderContainer from '../grid/SelectionHeaderContainer';
  
// defines the selections header prop definitions ...
export interface NotificationPanelHeaderProps extends BulkHeader {
  onBulkEdit?: (assets: any) => void;
  onBulkDelete?: (assets: any) => void;
  actionMenuItems?: ReactNode;
  onBulkAcknowledge?: (events: any) => void;
  handleSelectAll?: (event: MouseEvent<HTMLButtonElement>) => void;
  selectedRows: Array<any> | [];
  omitCTAs?: boolean;
  notificationsCheckAll?: boolean;
  disableBulkDelete?: boolean;
  customSelectionHeaderButton?: any;
  count: number;
};

export const useStyles = makeStyles((theme) => ({
  header : {
    justifyContent : 'space-evenly'
  },
  headerCount : {
    fontSize : '13px'
  },
  headerButtons : {
    display : 'contents'
  }
}));
  
/**
 * Defines the NotificationPanelHeader component. This component displays the selected item count
 * on top of the notification panel with options to bulk acknowledge/clear the selected notifications.
 * This component is displayed only when multiple items are selected.
 *
 * ```typescript
 * 
 *  import NotificationPanelHeader from '@ion/dls/src/notifications/NotificationPanelHeader'
 *
 *  <NotificationPanelHeader onBulkDelete={onBulkDelete} />
 *
 * ```
 */
export const NotificationPanelHeader = ({
  selectedRows,
  onBulkDelete,
  actionMenuItems,
  onBulkAcknowledge,
  handleSelectAll,
  customSelectionHeaderButton,
  count = selectedRows?.length,
  omitCTAs = false,
  notificationsCheckAll = false,
  disableBulkDelete = false
}: NotificationPanelHeaderProps) => {
  const classes   = useStyles();
  
  const onActionWithSelectedRows = useCallback((callback): MouseEventHandler =>
    () => callback?.(selectedRows),
  [selectedRows]);
  
  return (
    <SelectionHeaderContainer overrideClassName={classes.header} show >
      {!omitCTAs && (
        <SelectionHeaderButtons overrideClassName={classes.headerButtons}>
          {actionMenuItems || ''}
          {customSelectionHeaderButton?.()}
          {onBulkAcknowledge && (
            <HeaderButton onClick={onActionWithSelectedRows(onBulkAcknowledge)}
              label={translate('grids:selectionsHeader.markAsRead')} />
          )}
          {isNil(customSelectionHeaderButton) && onBulkDelete && (
            <HeaderButton disabled={disableBulkDelete}
              menuPaddingTop='6px'
              onClick={onActionWithSelectedRows(onBulkDelete)}
              label={translate('grids:selectionsHeader.clear')} />
          )}
          {handleSelectAll && (
            <HeaderButton onClick={onActionWithSelectedRows(handleSelectAll)}
              label={translate(`grids:selectionsHeader.${notificationsCheckAll ? 'deselectAll' : 'selectAll'}`)} />
          )}
        </SelectionHeaderButtons>
      )}
    </SelectionHeaderContainer>
  );
};
  
export default memo(NotificationPanelHeader);
